import AppIcon from "../components/AppIcon";
import { useState, useEffect, useRef, useCallback } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { LuUpload as FiUpload, LuVideo as FiVideo } from "react-icons/lu";
import { useParams, useSearchParams } from "react-router";
import {
    ref,
    onValue,
    push,
    set,
    update,
    get,
    query,
    orderByChild,
    equalTo,
} from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { LuCamera as FiCamera, LuMic as FiMic } from "react-icons/lu";
import {
    getDownloadURL,
    ref as storageRef,
    uploadBytesResumable,
} from "firebase/storage";
import { storage } from "../constants/firebase";
import useMoveTo from "../constants/navigation";
import { LuPaperclip as CgAttachment } from "react-icons/lu";
import CreatePoll from "../components/CreatePoll";
import PollMessage from "../components/PollMessage";
import { v4 as uuidv4 } from "uuid";
import {
    calculateStreakWithFreeze,
    compressImage,
    convertHeicToJpg,
    copyToClipboard,
    formatDate,
    formatTime,
    handleDownloadImage,
    parseMentions,
} from "../utils/utils";
import { LuEllipsisVertical as BsThreeDotsVertical } from "react-icons/lu";
import useKeyboardOpen from "../hooks/useKeyboardOpen";
import { ROUTES } from "../App";
import ViewMembers from "../components/ViewMembers";
import { LuChartNoAxesColumn as MdPoll } from "react-icons/lu";
import ReactionBox from "../components/ReactionBox";
import MessageBubble from "../components/MessageBubble";
import MessageInput from "../components/MessageInput";
import RecordingPopup from "../components/RecordingPopup";
import { useChatMessages } from "../hooks/useChatMessages";
import { useChatScroll } from "../hooks/useChatScroll";
import { useMessageActions } from "../hooks/useMessageActions";
import { useRecording } from "../hooks/useRecording";
import { formatGroupedMessages, groupMessagesByDate } from "../utils/chatUtils";
import SignInPopup from "../components/SignInPopup";
import ForwardSharePopup from "../components/ForwardSharePopup";

const CommunityChat = () => {
    const navigate = useMoveTo();
    const { communityId } = useParams();
    const { currentUser } = useUser();
    const [community, setCommunity] = useState(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [isHost, setIsHost] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const textareaRef = useRef(null);
    const [isRecording, setIsRecording] = useState(false);
    const [openPollModal, setOpenPollModal] = useState(false);
    const [copied, setCopied] = useState("");
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const isKeyboardOpen = useKeyboardOpen();
    const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get("invite");
    const [invitePopupOpen, setInvitePopupOpen] = useState(false);
    const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
    const [existingCommunityInvite, setExistingCommunityInvite] = useState(null);
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionSearch, setMentionSearch] = useState("");
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [communityMembers, setCommunityMembers] = useState([]);
    const [loadedImages, setLoadedImages] = useState({});
    const [allUsers, setAllUsers] = useState([]);
    const [showSignInPopup, setShowSignInPopup] = useState(false);
    const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);

    // Custom hooks
    const { messages, loading: messagesLoading, deleteMessage, addReaction, removeReaction, loadMoreMessages, loadingMore, noMoreMessages } =
        useChatMessages('community', communityId, currentUser);

    const { scrollContainerRef, setContainerRef, handleScroll, pullState } = useChatScroll(messages, loadMoreMessages);

    const {
        selectionMode,
        setSelectionMode,
        selectedMessage,
        setSelectedMessage,
        selectedMessageId,
        setSelectedMessageId,
        reactionPosition,
        editingMessage,
        cancelEdit,
        editMessageText,
        setEditMessageText,
        replyingTo,
        reactionBoxRef,
        longPressTimeout,
        longPressTriggered,
        isScrollingRef,
        openReactionBox,
        closeReactionBox,
        handleEditMessage,
        handleReplyToMessage,
        clearReply,
        isReactingRef
    } = useMessageActions(currentUser);

    const {
        isRecording: isVoiceRecording,
        recordingPopup,
        startRecording,
        stopRecording,
        cancelRecording,
        uploadAudio
    } = useRecording('community', communityId, currentUser);

    // Add this check after all your hooks and before the main return
    useEffect(() => {
        if (!currentUser && community) {
            setShowSignInPopup(true);
        } else {
            setShowSignInPopup(false);
        }
    }, [currentUser, community]);

    // Define sendMessage locally
    const sendMessage = useCallback(async (messageData) => {
        if (!communityId || !currentUser) return null;

        const messagesRef = ref(database, `communityMessages/${communityId}`);
        const newMessageRef = push(messagesRef);

        const defaultData = {
            senderId: currentUser.uid,
            timestamp: Date.now(),
            isSeen: { [currentUser.uid]: true }
        };

        await set(newMessageRef, { ...defaultData, ...messageData });
        return newMessageRef.key;
    }, [communityId, currentUser]);

    // Fetch all Zylen users for mentions
    useEffect(() => {
        if (!currentUser) return;

        const usersRef = ref(database, 'Users');
        const unsubscribe = onValue(usersRef, (snapshot) => {
            const usersData = snapshot.val() || {};
            const usersList = [];

            Object.entries(usersData).forEach(([uid, userData]) => {
                // Optionally filter out the current user
                if (userData?.email || userData.email !== null) {
                    if (uid !== currentUser?.uid) {
                        usersList.push({
                            uid: uid,
                            name: userData.name || userData.email || 'Unknown User',
                            profilePic: userData.profilePic,
                            // You can add other user properties if needed
                        });
                    }
                }
            });

            // Sort users by name alphabetically
            usersList.sort((a, b) => a.name.localeCompare(b.name));
            setAllUsers(usersList);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // Define editMessage locally
    const editMessage = useCallback(async (messageId, newText, mentions = []) => {
        if (!communityId || !currentUser || !messageId || !newText.trim()) return;

        const messageRef = ref(database, `communityMessages/${communityId}/${messageId}`);

        const updateData = {
            text: newText,
            edited: true,
            editedAt: Date.now()
        };

        if (mentions.length > 0) {
            updateData.mentions = mentions;
        }

        await update(messageRef, updateData);
    }, [communityId, currentUser]);

    const autoResizeTextarea = (el) => {
        el.style.height = "auto";
        const maxHeight = 112;
        if (el.scrollHeight > maxHeight) {
            el.style.height = maxHeight + "px";
            el.style.overflowY = "auto";
        } else {
            el.style.height = el.scrollHeight + "px";
            el.style.overflowY = "hidden";
        }
    };

    const resetTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
    };

    // Fetch community members
    useEffect(() => {
        if (!communityId) return;

        const membersRef = ref(database, `Communities/${communityId}/members`);
        const unsubscribe = onValue(membersRef, async (snapshot) => {
            const memberIds = snapshot.val() || {};
            const membersList = [];

            for (const memberId of Object.keys(memberIds)) {
                if (memberIds[memberId] === true) {
                    const userSnap = await get(ref(database, `Users/${memberId}`));
                    const userData = userSnap.val();
                    if (userData) {
                        membersList.push({
                            uid: memberId,
                            name: userData.name || userData.email,
                            profilePic: userData.profilePic
                        });
                    }
                }
            }
            setCommunityMembers(membersList);
        });

        return () => unsubscribe();
    }, [communityId]);

    // Handle input change with mention detection
    const handleInputChange = (e) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;
        setNewMessage(value);
        setCursorPosition(cursorPos);

        const textBeforeCursor = value.substring(0, cursorPos);
        const atIndex = textBeforeCursor.lastIndexOf('@');

        if (atIndex !== -1 && (atIndex === 0 || textBeforeCursor[atIndex - 1] === ' ')) {
            const searchText = textBeforeCursor.substring(atIndex + 1);
            const hasSpaceAfter = searchText.includes(' ');

            if (!hasSpaceAfter) {
                setMentionSearch(searchText);
                const filtered = allUsers.filter(member =>
                    member.uid !== currentUser?.uid &&
                    member.name.toLowerCase().includes(searchText.toLowerCase())
                );
                setMentionSuggestions(filtered);
                setShowMentions(filtered.length > 0);
                setMentionStartIndex(atIndex);
            } else {
                setShowMentions(false);
            }
        } else {
            setShowMentions(false);
        }

        autoResizeTextarea(e.target);
    };

    // Handle mention selection
    const handleMentionSelect = (member) => {
        const beforeMention = newMessage.substring(0, mentionStartIndex);
        const afterMention = newMessage.substring(cursorPosition);
        const newText = `${beforeMention}@${member.name} ${afterMention}`;
        setNewMessage(newText);
        setShowMentions(false);

        setTimeout(() => {
            if (textareaRef.current) {
                const newCursorPos = mentionStartIndex + member.name.length + 2;
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };

    // Check invite token
    useEffect(() => {
        if (!inviteToken || !currentUser || !community) return;

        // If current user is the host, do nothing (don't show invite popup)
        if (currentUser.uid === community.hostId) {
            clearInviteParam(); // Remove invite param from URL
            return;
        }

        const checkMembership = async () => {
            try {
                const memberSnap = await get(
                    ref(database, `Communities/${communityId}/members/${currentUser.uid}`)
                );

                if (memberSnap.exists()) {
                    const isMember = memberSnap.val();
                    if (isMember) {
                        window.showToast("You are already a member of this community", "error");
                        clearInviteParam();
                    } else {
                        setInvitePopupOpen(true);
                    }
                } else {
                    setInvitePopupOpen(true);
                }
            } catch (err) {
                console.error("Failed to check membership:", err);
                setInvitePopupOpen(true);
            }
        };

        checkMembership();
    }, [inviteToken, currentUser, community, communityId]);

    const handleInviteJoin = async (token) => {
        try {
            const inviteSnap = await get(ref(database, `Invites/${token}`));
            if (!inviteSnap.exists()) {
                window.showToast("Invalid invite link", "error");
                return;
            }

            const { targetType, targetId } = inviteSnap.val();

            if (targetType === "community" && targetId !== communityId) {
                window.showToast("Invite does not match this page", "error");
                return;
            }

            if (targetType === "community") {
                await joinCommunityWithCheck(targetId);
            }
        } catch (err) {
            console.error(err);
            window.showToast("Failed to join", "error");
        }
    };

    const joinCommunityWithCheck = async (communityId) => {
        const memberRef = ref(database, `Communities/${communityId}/members/${currentUser.uid}`);
        const snap = await get(memberRef);

        if (snap.exists() && snap.val()) {
            window.showToast("You are already a member", "error");
            return;
        }

        const updates = {};
        updates[`Communities/${communityId}/members/${currentUser.uid}`] = true;
        updates[`Users/${currentUser.uid}/joinedCommunities/${communityId}`] = true;

        await update(ref(database), updates);
        window.showToast("Joined community", "success");
        clearInviteParam();
    };

    const clearInviteParam = () => {
        navigate(`${ROUTES.COMMUNITY_CHAT}/${communityId}`, { replace: true });
    };

    useEffect(() => {
        if (currentUser && communityId) {
            const lastSeenRef = ref(database, `Users/${currentUser.uid}/lastSeen/${communityId}`);
            update(lastSeenRef, { timestamp: Date.now() });
        }
    }, [communityId, currentUser]);

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (editingMessage) return;
            if (event.button === 2) return;

            if (!editingMessage) {
                setSelectionMode(false);
                setSelectedMessage(null);
                setSelectedMessageId(null);
            }

            if (reactionBoxRef.current && !reactionBoxRef.current.contains(event.target)) {
                setSelectedMessage(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [selectedMessageId, editingMessage, reactionBoxRef, setSelectionMode, setSelectedMessage, setSelectedMessageId]);

    // Fetch community data
    useEffect(() => {
        if (!communityId) return;

        const communityRef = ref(database, `Communities/${communityId}`);
        const unsubscribeCommunity = onValue(communityRef, (snapshot) => {
            const data = snapshot.val();
            setCommunity(data);
            setIsHost(currentUser?.uid === data?.hostId);
        });

        if (currentUser) {
            const followRef = ref(database, `Communities/${communityId}/members/${currentUser.uid}`);
            onValue(followRef, (snapshot) => {
                setIsFollowing(!!snapshot.val());
            });
        }

        return () => {
            unsubscribeCommunity();
        };
    }, [communityId, currentUser]);

    // Handle follow/unfollow
    const handleFollow = async () => {
        if (!currentUser || !communityId || !community) return;

        try {
            const updates = {};
            const now = new Date().toISOString();
            const joining = !isFollowing;

            updates[`Communities/${communityId}/members/${currentUser.uid}`] =
                joining ? true : null;

            updates[`Users/${currentUser.uid}/joinedCommunities/${communityId}`] =
                joining ? true : null;

            // 🔥 Correct join date logic
            updates[`Communities/${communityId}/memberJoinDates/${currentUser.uid}`] =
                joining ? now : null;

            updates[`Communities/${communityId}/memberCount`] =
                joining
                    ? (community.memberCount || 0) + 1
                    : Math.max(0, (community.memberCount || 0) - 1);

            await update(ref(database), updates);

        } catch (error) {
            console.error("Error updating follow status:", error);
        }
    };
    // Format messages
    const formatMessages = (messages) => {
        if (!messages.length) return [];

        const formattedMessages = [];
        let lastSenderId = null;

        messages.forEach((message, index) => {
            const showHeader = lastSenderId !== message.senderId ||
                (index > 0 && messages[index - 1].senderId !== message.senderId);

            formattedMessages.push({ ...message, showHeader });
            lastSenderId = message.senderId;
        });

        return formattedMessages;
    };

    const groupedMessages = groupMessagesByDate(messages, formatDate);
    const formattedGroupedMessages = formatGroupedMessages(groupedMessages, formatMessages);

    // Handle sending message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || !currentUser || !communityId) return;

        try {
            const mentions = parseMentions(newMessage, allUsers, currentUser);
            const communityRef = ref(database, `Communities/${communityId}`);
            const communitySnap = await get(communityRef);
            const communityData = communitySnap.val();

            const lastActivity = communityData?.lastActivity && communityData.lastActivity !== ""
                ? Number(communityData.lastActivity)
                : null;

            const streakData = calculateStreakWithFreeze(
                lastActivity,
                communityData?.streakCount ?? 0,
                communityData?.freezeUsed ?? 0,
                2
            );

            const messageData = {
                text: newMessage,
                senderName: currentUser.name || currentUser.email,
                senderAvatar: currentUser.profilePic || null,
                isHost: currentUser.uid === community?.hostId,
                mentions
            };

            if (replyingTo) {
                messageData.replyTo = {
                    messageId: replyingTo.id,
                    senderId: replyingTo.senderId,
                };

                if (replyingTo.text) messageData.replyTo.text = replyingTo.text;
                else if (replyingTo.imageUrl) {
                    messageData.replyTo.imageUrl = replyingTo.imageUrl;
                    messageData.replyTo.displayText = "📷 Photo";
                } else if (replyingTo.audioUrl) {
                    messageData.replyTo.audioUrl = replyingTo.audioUrl;
                    messageData.replyTo.displayText = "🎤 Voice message";
                }
            }

            await sendMessage(messageData);

            setTimeout(async () => {
                await update(communityRef, {
                    streakCount: streakData.streakCount,
                    freezeUsed: streakData.freezeUsed,
                    lastActivity: Date.now(),
                });
            }, 500);

            setNewMessage("");
            clearReply();
            resetTextareaHeight();
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    // Handle image upload
    const handleImageUpload = async (file) => {
        if (!file || !currentUser || !communityId) return;

        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
            "image/heic",
            "image/heif"
        ];

        if (!allowedTypes.includes(file.type)) {
            window.showToast("Unsupported image format", "error");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            let processedFile = file;

            if (file.type === "image/heic" || file.type === "image/heif") {
                processedFile = await convertHeicToJpg(file);
            } else {
                processedFile = await compressImage(file);
            }

            const filename = `${Date.now()}_${processedFile.name}`;
            const imageRef = storageRef(storage, `communityMessages/${communityId}/${filename}`);

            const uploadTask = uploadBytesResumable(imageRef, processedFile);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Error uploading image:", error);
                    setIsUploading(false);
                },
                async () => {
                    try {
                        const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                        const communityRef = ref(database, `Communities/${communityId}`);
                        const communitySnap = await get(communityRef);
                        const communityData = communitySnap.val();

                        const lastActivity = communityData?.lastActivity && communityData.lastActivity !== ""
                            ? Number(communityData.lastActivity)
                            : null;

                        const streakData = calculateStreakWithFreeze(
                            lastActivity,
                            communityData?.streakCount ?? 0,
                            communityData?.freezeUsed ?? 0,
                            2
                        );

                        console.log("StreakData: ", streakData);


                        await update(communityRef, {
                            streakCount: streakData.streakCount,
                            freezeUsed: streakData.freezeUsed,
                            lastActivity: Date.now(),
                        });

                        await sendMessage({
                            imageUrl,
                            senderName: currentUser.name || currentUser.email,
                            senderAvatar: currentUser.profilePic || null,
                            isHost: currentUser.uid === community?.hostId,
                        });

                        setIsUploading(false);
                        setUploadProgress(0);
                    } catch (error) {
                        console.error("Error getting download URL:", error);
                        setIsUploading(false);
                    }
                }
            );
        } catch (error) {
            console.error("Error starting upload:", error);
            window.showToast(`Failed to upload image: ${error.message}`, "error");
            setIsUploading(false);
        }
    };

    const handleVideoUpload = async (file) => {
        if (!file || !currentUser || !communityId) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const filename = `${Date.now()}_${file.name}`;
            const videoRef = storageRef(storage, `communityMessages/${communityId}/${filename}`);
            const uploadTask = uploadBytesResumable(videoRef, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Error uploading video:", error);
                    setIsUploading(false);
                },
                async () => {
                    const videoUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    const communityRef = ref(database, `Communities/${communityId}`);
                    const communitySnap = await get(communityRef);
                    const communityData = communitySnap.val();

                    const lastActivity = communityData?.lastActivity && communityData.lastActivity !== ""
                        ? Number(communityData.lastActivity)
                        : null;

                    const streakData = calculateStreakWithFreeze(
                        lastActivity,
                        communityData?.streakCount ?? 0,
                        communityData?.freezeUsed ?? 0,
                        2
                    );

                    await update(communityRef, {
                        streakCount: streakData.streakCount,
                        freezeUsed: streakData.freezeUsed,
                        lastActivity: Date.now(),
                    });

                    await sendMessage({
                        videoUrl,
                        senderName: currentUser.name || currentUser.email,
                        senderAvatar: currentUser.profilePic || null,
                        isHost: currentUser.uid === community?.hostId,
                    });

                    setIsUploading(false);
                    setUploadProgress(0);
                }
            );
        } catch (error) {
            console.error("Error uploading video:", error);
            window.showToast(`Failed to upload video: ${error.message}`, "error");
            setIsUploading(false);
        }
    };

    const handleAudioFileUpload = async (file) => {
        if (!file || !currentUser || !communityId) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const filename = `${Date.now()}_${file.name}`;
            const audioRef = storageRef(storage, `communityMessages/${communityId}/${filename}`);
            const uploadTask = uploadBytesResumable(audioRef, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Error uploading audio:", error);
                    setIsUploading(false);
                },
                async () => {
                    const audioUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    const communityRef = ref(database, `Communities/${communityId}`);
                    const communitySnap = await get(communityRef);
                    const communityData = communitySnap.val();

                    const lastActivity = communityData?.lastActivity && communityData.lastActivity !== ""
                        ? Number(communityData.lastActivity)
                        : null;

                    const streakData = calculateStreakWithFreeze(
                        lastActivity,
                        communityData?.streakCount ?? 0,
                        communityData?.freezeUsed ?? 0,
                        2
                    );

                    await update(communityRef, {
                        streakCount: streakData.streakCount,
                        freezeUsed: streakData.freezeUsed,
                        lastActivity: Date.now(),
                    });

                    await sendMessage({
                        audioUrl,
                        senderName: currentUser.name || currentUser.email,
                        senderAvatar: currentUser.profilePic || null,
                        isHost: currentUser.uid === community?.hostId,
                    });

                    setIsUploading(false);
                    setUploadProgress(0);
                }
            );
        } catch (error) {
            console.error("Error uploading audio:", error);
            window.showToast(`Failed to upload audio: ${error.message}`, "error");
            setIsUploading(false);
        }
    };

    const createPoll = async ({ question, options, allowMultiple }) => {
        if (!currentUser || !communityId) return;

        const pollId = uuidv4();
        const optionsArr = options.map((text, index) => ({
            id: uuidv4(),
            text,
            order: index,
        }));

        const communityRef = ref(database, `Communities/${communityId}`);
        const communitySnap = await get(communityRef);
        const communityData = communitySnap.val();

        const lastActivity = communityData?.lastActivity && communityData.lastActivity !== ""
            ? Number(communityData.lastActivity)
            : null;

        const streakData = calculateStreakWithFreeze(
            lastActivity,
            communityData?.streakCount ?? 0,
            communityData?.freezeUsed ?? 0,
            2
        );

        await update(communityRef, {
            streakCount: streakData.streakCount,
            freezeUsed: streakData.freezeUsed,
            lastActivity: Date.now(),
        });

        const pollRef = ref(database, `polls/${communityId}/${pollId}`);
        await set(pollRef, {
            question,
            allowMultiple,
            options: optionsArr,
            createdBy: currentUser.uid,
            timestamp: Date.now(),
        });

        await sendMessage({
            type: "poll",
            pollId,
            senderName: currentUser.name || currentUser.email,
            senderAvatar: currentUser.profilePic || null,
            isHost: currentUser.uid === community?.hostId,
        });
    };

    // Load existing invite
    useEffect(() => {
        if (!currentUser || !communityId) return;

        const loadExistingInvite = async () => {
            try {
                const targetType = "community";
                const compositeKey = `${targetType}_${communityId}_${currentUser.uid}`;
                const invitesRef = ref(database, "Invites");
                const inviteQuery = query(
                    invitesRef,
                    orderByChild("compositeKey"),
                    equalTo(compositeKey)
                );

                const snapshot = await get(inviteQuery);

                if (snapshot.exists()) {
                    const inviteKey = Object.keys(snapshot.val())[0];
                    const inviteUrl = `${window.location.origin}/#/community/${communityId}?invite=${inviteKey}`;
                    setExistingCommunityInvite(inviteUrl);
                }
            } catch (err) {
                console.error("Community invite preload error:", err);
            }
        };

        loadExistingInvite();
    }, [currentUser.uid, communityId, currentUser]);

    const handleInvite = () => {
        if (!currentUser || !communityId) return;

        if (existingCommunityInvite) {
            copyToClipboard(existingCommunityInvite)
                .then(() => {
                    setCopied("invited");
                    setTimeout(() => setCopied(""), 2000);
                    window.showToast("Invite link copied", "success");
                })
                .catch(() => window.showToast("Failed to copy invite link", "error"));
            return;
        }

        const targetType = "community";
        const targetId = communityId;
        const compositeKey = `${targetType}_${targetId}_${currentUser.uid}`;
        const invitesRef = ref(database, "Invites");

        const tempInviteRef = push(invitesRef);
        const inviteToken = tempInviteRef.key;
        const inviteUrl = `${window.location.origin}/#/community/${targetId}?invite=${inviteToken}`;

        copyToClipboard(inviteUrl)
            .then(() => {
                setCopied("invited");
                setTimeout(() => setCopied(""), 2000);
                window.showToast("Invite link copied", "success");
            })
            .catch(() => window.showToast("Failed to copy invite link", "error"));

        setExistingCommunityInvite(inviteUrl);

        (async () => {
            try {
                await set(tempInviteRef, {
                    targetType,
                    targetId,
                    createdBy: currentUser.uid,
                    compositeKey,
                    createdAt: Date.now(),
                });
            } catch (error) {
                console.error("Invite save error:", error);
            }
        })();
    };

    const handleReaction = async (messageId, emoji) => {
        await addReaction(messageId, emoji.id);
        setSelectedMessage(null);
        setSelectionMode(false);
    };

    const toggleReaction = async (messageId, emojiId, currentReaction) => {
        if (currentReaction === emojiId) {
            await removeReaction(messageId);
        } else {
            await addReaction(messageId, emojiId);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!isHost) {
            console.warn("Not host, cannot delete");
            return;
        }
        console.log("Message: id: ", messageId);
        await deleteMessage(messageId);
        setSelectionMode(false);
        setSelectedMessageId(null);
    };

    const handleUpdateMessage = async () => {
        if (!editMessageText.trim() || !currentUser || !communityId || !editingMessage) return;

        try {
            const mentions = parseMentions(editMessageText, allUsers, currentUser);
            await editMessage(editingMessage.id, editMessageText, mentions);
            cancelEdit();
            setSelectionMode(false);
            setNewMessage("");
            resetTextareaHeight();
        } catch (error) {
            console.error("Error updating message:", error);
            window.showToast("Failed to update message", "error");
        }
    };

    const [newMessage, setNewMessage] = useState("");

    const handleGoBack = () => {
        // Try to go back, if there's no history, go to Chats
        if (window.history.length > 2) { // >2 because typically there's at least the current page and the previous
            navigate(-1);
        } else {
            navigate(ROUTES.DASHBOARD);
        }
    };

    const handleForwardShare = async (selectedItems) => {
        try {
            // Get the invite link for the current community
            let shareLink = existingCommunityInvite;
            let inviteToken = null;

            // If no existing invite link, create one
            if (!shareLink) {
                const targetType = "community";
                const targetId = communityId;
                const compositeKey = `${targetType}_${targetId}_${currentUser.uid}`;
                const invitesRef = ref(database, "Invites");

                const tempInviteRef = push(invitesRef);
                inviteToken = tempInviteRef.key;
                shareLink = `${window.location.origin}/#/community/${targetId}?invite=${inviteToken}`;

                await set(tempInviteRef, {
                    targetType,
                    targetId,
                    createdBy: currentUser.uid,
                    compositeKey,
                    createdAt: Date.now(),
                });

                setExistingCommunityInvite(shareLink);
            }

            // Prepare share message
            const shareMessage = `Follow this link to join my zylen community: \n${shareLink}`;

            // Share to selected items
            for (const item of selectedItems) {
                if (item.type === 'chat') {
                    // Send to private chat
                    const chatId = [currentUser.uid, item.id].sort().join('_');
                    const messagesRef = ref(database, `privateChats/${chatId}`);
                    const newMessageRef = push(messagesRef);

                    await set(newMessageRef, {
                        senderId: currentUser.uid,
                        text: shareMessage,
                        timestamp: Date.now(),
                        seen: false,
                        type: 'forwarded',
                        originalCommunity: {
                            id: communityId,
                            name: community.name,
                            photo: community.imageUrl || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
                            inviteToken: inviteToken,
                        }
                    });
                }
                else if (item.type === 'group') {
                    // Send to group chat
                    const messagesRef = ref(database, `groupChats/${item.id}`);
                    const newMessageRef = push(messagesRef);

                    await set(newMessageRef, {
                        senderId: currentUser.uid,
                        text: shareMessage,
                        timestamp: Date.now(),
                        seenBy: { [currentUser.uid]: true },
                        type: 'forwarded',
                        originalCommunity: {
                            id: communityId,
                            name: community.name,
                            photo: community.imageUrl || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
                            inviteToken: inviteToken,
                        }
                    });
                }
                else if (item.type === 'community') {
                    // Send to community chat
                    const messagesRef = ref(database, `communityMessages/${item.id}`);
                    const newMessageRef = push(messagesRef);

                    await set(newMessageRef, {
                        senderId: currentUser.uid,
                        text: shareMessage,
                        timestamp: Date.now(),
                        isSeen: { [currentUser.uid]: true },
                        type: 'forwarded',
                        originalCommunity: {
                            id: communityId,
                            name: community.name,
                            photo: community.imageUrl || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
                            inviteToken: inviteToken,
                        }
                    });
                }
            }

            window.showToast(`Shared to ${selectedItems.length} chat(s)`, "success");
        } catch (error) {
            console.error("Error sharing community:", error);
            window.showToast("Failed to share community", "error");
        }
    };

    useEffect(() => {
        console.log("No More Messages updated:", noMoreMessages);
    }, [noMoreMessages]);

    if (!community || messagesLoading) {
        return (
            <div className="h-screen flex items-center justify-center text-gray-500">
                Loading...
            </div>
        );
    }

    const menuItems = [
        {
            label: "Share Community",
            onClick: () => {
                setIsMenuOpen(false);
                setIsSharePopupOpen(true);
            },
        },
        {
            label: copied === "invited" ? "Copied!" : "Copy Invite Link",
            onClick: handleInvite
        }
    ];



    return (
        <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white overflow-hidden">
            <div className="flex h-full">
                <Sidebar />

                <main className="flex-1 h-full flex flex-col bg-white dark:bg-[#23272c] relative min-w-0">
                    {/* Header */}
                    <div className="zy-native-chat-header flex items-center justify-between px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-8 gap-2 sm:gap-4">
                        <div className="flex items-center min-w-0 flex-1">
                            <button
                                onClick={handleGoBack}
                                className="flex items-center justify-center p-1.5 sm:p-2 flex-shrink-0"
                            >
                                <AppIcon
                                    name="arrowBack"
                                    className="w-4 h-4 sm:w-5 sm:h-5 brightness-0 dark:invert"
                                    alt="Back"
                                />
                            </button>
                            <div className="flex items-center min-w-0 ml-1 sm:ml-2 md:ml-3 flex-1">
                                <button onClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${community.hostId}`)}>
                                    <img
                                        src={community.imageUrl || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"}
                                        alt={community.name}
                                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0 object-cover cursor-pointer"
                                        onClick={() => setSelectedImage(community.imageUrl)}
                                    />
                                </button>
                                <div className="flex flex-col min-w-0 ml-2 sm:ml-3">
                                    <button onClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${community.hostId}`)}>
                                        <p className="font-bold text-sm sm:text-base md:text-lg dark:text-white text-black truncate">
                                            {community.name}
                                        </p>
                                    </button>
                                    <button className="flex items-center" onClick={() => setIsMembersModalOpen(true)}>
                                        <p className="text-xs sm:text-sm dark:text-white/40 text-primary truncate cursor-pointer">
                                            {community.memberCount || 0} {community.memberCount === 1 ? "member" : "members"}
                                        </p>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            {currentUser?.uid && !isHost && (
                                <button
                                    onClick={handleFollow}
                                    className={`py-1 px-2 sm:px-3 md:px-4 text-xs sm:text-sm md:text-[15px] rounded-md whitespace-nowrap ${isFollowing ? "bg-gray-600 text-white" : "bg-primary text-white"}`}
                                >
                                    {isFollowing ? "Joined" : "Join"}
                                </button>
                            )}
                            {isHost && (
                                <div className="relative" ref={menuRef}>
                                    <button
                                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                                        className="p-1.5 sm:p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                    >
                                        <BsThreeDotsVertical className="w-4 h-4 sm:w-5 sm:h-5 text-gray-800 dark:text-white" />
                                    </button>
                                    {isMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white dark:bg-[#2A2A2A] rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                                            <ul className="py-1">
                                                {menuItems.map((item, index) => (
                                                    <li key={index}>
                                                        <button
                                                            onClick={item.onClick}
                                                            className="w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500/30 whitespace-nowrap"
                                                        >
                                                            {item.label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Chat Content */}
                    <div
                        ref={setContainerRef}
                        onScroll={handleScroll}   // ✅ ADD THIS LINE
                        className={`flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 md:px-6 py-4 sm:py-6 mt-3 ${isKeyboardOpen ? "pb-20" : "pb-36"} md:pb-0`}
                        style={{
                            backgroundImage: `linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.9)), url(${IMAGES.chatBg})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            overflowX: "hidden", // 🔥 prevents horizontal movement
                            overflowAnchor: "auto", // 🔥 prevents auto scroll on new messages
                        }}
                    >
                        {!noMoreMessages && (
                            <div className="flex justify-center items-center py-2 text-white/70 text-sm">
                                {pullState.loading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Loading...
                                    </div>
                                ) : pullState.ready ? (
                                    "Release to load more"
                                ) : (
                                    "Pull to load more"
                                )}
                            </div>
                        )}
                        {formattedGroupedMessages.map(({ date, messages }) => (
                            <div key={date} className="mb-4 sm:mb-6">
                                <div className="flex items-center justify-center mb-2 sm:mb-3">
                                    <div className="bg-white/20 text-white px-4 sm:px-6 md:px-8 py-1 rounded-lg text-xs sm:text-sm font-medium backdrop-blur-md">
                                        {date}
                                    </div>
                                </div>

                                <div className="space-y-3 sm:space-y-4 z-10 relative">
                                    {messages.map((message) => {
                                        let isFromHost = false;
                                        if (currentUser?.uid) {
                                            isFromHost = message.senderId === currentUser.uid;
                                        }

                                        return (
                                            <div
                                                key={message.id}
                                                data-message-id={message.id}
                                                className={`flex items-start gap-2 sm:gap-3 md:gap-4 ${isFromHost ? "justify-end" : "justify-start"}`}
                                                onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openReactionBox(message.id, e.clientX, e.clientY, message);
                                                }}
                                                onTouchStart={(e) => {
                                                    const touch = e.touches[0];
                                                    // Clear any existing timeout
                                                    if (longPressTimeout.current) {
                                                        clearTimeout(longPressTimeout.current);
                                                    }

                                                    // Set new timeout
                                                    longPressTimeout.current = setTimeout(() => {
                                                        longPressTriggered.current = true; // 👈 THIS IS CRITICAL - set to true when long press succeeds
                                                        openReactionBox(
                                                            message.id,
                                                            touch.clientX,
                                                            touch.clientY,
                                                            message
                                                        );
                                                    }, 600);
                                                }}
                                                onTouchMove={(e) => {
                                                    const touch = e.touches[0];
                                                    const dx = Math.abs(touch.clientX - 0);
                                                    const dy = Math.abs(touch.clientY - 0);

                                                    if (dx > 10 || dy > 10) {
                                                        isScrollingRef.current = true;
                                                        clearTimeout(longPressTimeout.current);
                                                    }
                                                }}
                                                onTouchEnd={() => {
                                                    clearTimeout(longPressTimeout.current);

                                                    // Check if this was a long press or reaction
                                                    if (isReactingRef.current || longPressTriggered.current || isScrollingRef.current) {
                                                        // Reset all flags
                                                        isReactingRef.current = false;
                                                        longPressTriggered.current = false;
                                                        isScrollingRef.current = false;
                                                        return; // Don't open image
                                                    }

                                                    // If not a long press or reaction, then open image
                                                    if (message.imageUrl) {
                                                        setSelectedImage(message.imageUrl);
                                                    }

                                                    // Reset flags
                                                    isScrollingRef.current = false;
                                                    longPressTriggered.current = false;
                                                }}
                                                onTouchCancel={() => {
                                                    clearTimeout(longPressTimeout.current);
                                                    isScrollingRef.current = false;
                                                    longPressTriggered.current = false;
                                                }}
                                            >

                                                <MessageBubble
                                                    message={message}
                                                    isFromCurrentUser={isFromHost}
                                                    currentUserId={currentUser?.uid}
                                                    senderName={message.senderName}
                                                    senderAvatar={message.senderAvatar || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"}
                                                    showHeader={message.showHeader}
                                                    onAvatarClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${message.senderId}`)}
                                                    onImageClick={() => message.imageUrl && setSelectedImage(message.imageUrl)}
                                                    selectionMode={selectionMode}
                                                    selected={selectedMessage?.id === message.id}
                                                    onToggleReaction={toggleReaction}
                                                    chatType="community"
                                                    loadedImages={loadedImages[message.id]}
                                                    onImageLoad={() => setLoadedImages(prev => ({ ...prev, [message.id]: true }))}
                                                    renderCustomContent={(msg) =>
                                                        msg.type === "poll" && msg.pollId && (
                                                            <div className={`mt-1 sm:mt-2 ${isFromHost ? "ml-auto" : ""}`}>
                                                                <div className={`rounded-[1.1rem] ${selectionMode && selectedMessage?.id === msg.id ? "border-2 border-[#57a1be]" : ""}`}>
                                                                    <PollMessage
                                                                        communityId={communityId}
                                                                        pollId={msg.pollId}
                                                                        currentUser={currentUser}
                                                                        isDark
                                                                        isHost={isFromHost}
                                                                    />
                                                                </div>
                                                                <p className="mt-1 text-right text-xs text-gray-400">
                                                                    {formatTime(msg.timestamp)}
                                                                </p>
                                                            </div>
                                                        )
                                                    }
                                                />

                                                {/* Reaction Box */}
                                                {selectedMessageId === message.id && (
                                                    <ReactionBox
                                                        position={reactionPosition}
                                                        isFromCurrentUser={isFromHost}
                                                        message={message}
                                                        currentUserId={currentUser.uid}
                                                        onReact={handleReaction}
                                                        onReply={handleReplyToMessage}
                                                        onEdit={handleEditMessage}
                                                        onDelete={handleDeleteMessage}
                                                        onClose={closeReactionBox}
                                                        boxRef={reactionBoxRef}
                                                        isHost={isHost}
                                                        showReply={false}
                                                        type={"community"}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Message Input */}
                    {isHost && (
                        <div className={`dark:border-t border-none border-[#2A2A2A] bg-white dark:bg-[#23272c] p-4 pb-6 md:pb-4 fixed ${isKeyboardOpen ? "bottom-0" : "bottom-18"} md:static left-0 right-0 z-10`}>
                            <div className="flex items-center gap-1 sm:gap-2 md:gap-4 max-w-full relative">
                                {/* Attachment Button */}
                                <div className="relative flex-shrink-0">
                                    <button
                                        onClick={() => setIsAttachmentMenuOpen(!isAttachmentMenuOpen)}
                                        className="p-1.5 sm:p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30 text-gray-800 dark:text-white"
                                    >
                                        <CgAttachment className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>

                                    {isAttachmentMenuOpen && (
                                        <div className="absolute bottom-10 sm:bottom-12 left-0 w-48 md:w-64 bg-white dark:bg-[#2A2A2A] rounded-xl shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                                            <div className="grid grid-cols-4 gap-1 sm:gap-2 p-1 sm:p-2">
                                                <button
                                                    onClick={() => {
                                                        imageInputRef.current?.click();
                                                        setIsAttachmentMenuOpen(false);
                                                    }}
                                                    className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                                    disabled={isUploading}
                                                >
                                                    <FiCamera className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                                                    <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">Image</span>
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        videoInputRef.current?.click();
                                                        setIsAttachmentMenuOpen(false);
                                                    }}
                                                    className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                                >
                                                    <FiVideo className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                                                    <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">Video</span>
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        audioInputRef.current?.click();
                                                        setIsAttachmentMenuOpen(false);
                                                    }}
                                                    className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                                >
                                                    <FiUpload className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                                                    <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">Audio</span>
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setOpenPollModal(true);
                                                        setIsAttachmentMenuOpen(false);
                                                    }}
                                                    className="flex flex-col items-center justify-center gap-1 p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                                >
                                                    <MdPoll className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800 dark:text-white" />
                                                    <span className="text-[10px] sm:text-xs md:text-sm text-gray-800 dark:text-white">Poll</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Mention Suggestions */}
                                {showMentions && mentionSuggestions.length > 0 && (
                                    <div
                                        className="absolute bottom-full left-10 md:left-16 mb-2 w-48 md:w-64 bg-white dark:bg-[#2A2A2A] rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[9999]"
                                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                                    >
                                        {mentionSuggestions.map((member) => (
                                            <div
                                                key={member.uid}
                                                className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                onClick={() => handleMentionSelect(member)}
                                            >
                                                <img
                                                    src={member.profilePic || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"}
                                                    alt={member.name}
                                                    className="w-5 h-5 md:w-6 md:h-6 rounded-full object-cover"
                                                />
                                                <span className="flex-1 min-w-0 text-xs md:text-sm text-gray-800 dark:text-white truncate">
                                                    {member.name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <MessageInput
                                    ref={textareaRef}
                                    value={editingMessage ? editMessageText : newMessage}
                                    onChange={(e) => {
                                        if (editingMessage) {
                                            setEditMessageText(e.target.value);
                                        } else {
                                            handleInputChange(e);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            if (showMentions && mentionSuggestions.length > 0) {
                                                handleMentionSelect(mentionSuggestions[0]);
                                            } else {
                                                const { selectionStart, selectionEnd } = e.target;
                                                const value = editingMessage ? editMessageText : newMessage;
                                                const newValue =
                                                    value.substring(0, selectionStart) + "\n" + value.substring(selectionEnd);

                                                if (editingMessage) {
                                                    setEditMessageText(newValue);
                                                } else {
                                                    setNewMessage(newValue);
                                                }

                                                setTimeout(() => {
                                                    e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
                                                }, 0);
                                            }
                                            autoResizeTextarea(e.target);
                                        }
                                    }}
                                    placeholder="Type a message..."
                                    isUploading={isUploading}
                                    onCameraClick={() => imageInputRef.current.click()}
                                    onSend={() => editingMessage ? handleUpdateMessage() : handleSendMessage()}
                                    onRecordStart={startRecording}
                                    isRecording={isVoiceRecording}
                                    replyingTo={replyingTo}
                                    onClearReply={clearReply}
                                    currentUserId={currentUser.uid}
                                    senderName={replyingTo?.senderId === currentUser.uid ? "You" : replyingTo?.senderName}
                                    showCamera={false}
                                />

                                {/* Hidden file inputs for attachments */}
                                <input
                                    type="file"
                                    ref={imageInputRef}
                                    accept="image/*"
                                    onChange={(e) => {
                                        if (e.target.files[0]) handleImageUpload(e.target.files[0]);
                                        e.target.value = null; // allow re-selecting the same file again
                                    }}
                                    className="hidden"
                                    disabled={isUploading}
                                />
                                <input
                                    type="file"
                                    ref={videoInputRef}
                                    accept="video/*"
                                    onChange={(e) => {
                                        if (e.target.files[0]) handleVideoUpload(e.target.files[0]);
                                        e.target.value = null;
                                    }}
                                    className="hidden"
                                    disabled={isUploading}
                                />
                                <input
                                    type="file"
                                    ref={audioInputRef}
                                    accept="audio/*"
                                    onChange={(e) => {
                                        if (e.target.files[0]) handleAudioFileUpload(e.target.files[0]);
                                        e.target.value = null;
                                    }}
                                    className="hidden"
                                    disabled={isUploading}
                                />
                            </div>

                            {recordingPopup && (
                                <RecordingPopup
                                    onCancel={cancelRecording}
                                    onSend={stopRecording}
                                />
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Modals and Overlays */}
            <CreatePoll
                open={openPollModal}
                onClose={() => setOpenPollModal(false)}
                onCreate={createPoll}
            />

            <ViewMembers
                isOpen={isMembersModalOpen}
                onClose={() => setIsMembersModalOpen(false)}
                membersPath={`Communities/${communityId}/members`}
                hostId={community?.hostId}
                title="Community Members"
            />

            {isUploading && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-[#23272c] p-4 sm:p-6 rounded-2xl flex flex-col items-center gap-3 sm:gap-4 shadow-lg">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 border-4 border-gray-300 border-t-[#327691] rounded-full animate-spin"></div>
                        <span className="text-sm sm:text-base text-gray-800 dark:text-white">Sending... {Math.round(uploadProgress)}%</span>
                    </div>
                </div>
            )}

            {selectedImage && (
                <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
                    <img
                        src={selectedImage}
                        alt="Full view"
                        className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-lg object-contain"
                    />
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute top-3 sm:top-5 right-3 sm:right-5 text-white text-2xl sm:text-3xl font-bold"
                    >
                        ✕
                    </button>
                    <button
                        onClick={() => handleDownloadImage(selectedImage)}
                        className="absolute bottom-5 right-5 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition"
                        title="Download image"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
                        </svg>
                    </button>
                </div>
            )}

            {invitePopupOpen && community && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
                    <div className="bg-white dark:bg-[#23272c] p-4 sm:p-6 rounded-xl w-full max-w-sm sm:max-w-md shadow-lg relative">
                        <button
                            onClick={() => { setInvitePopupOpen(false); clearInviteParam(); }}
                            className="flex justify-self-end mb-2 text-gray-500 hover:text-gray-700 dark:hover:text-white text-xl font-bold"
                        >
                            ✕
                        </button>
                        <h2 className="text-base sm:text-lg font-semibold text-center mb-3 sm:mb-4 dark:text-white">
                            You've been invited to join {community.name}
                        </h2>
                        <p className="text-center mb-4 sm:mb-6 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                            Click below to join the community.
                        </p>
                        <button
                            onClick={async () => {
                                await handleInviteJoin(inviteToken);
                                setInvitePopupOpen(false);
                            }}
                            className="w-full bg-primary text-white py-1.5 sm:py-2 rounded-lg hover:bg-primary/90 transition text-sm sm:text-base"
                        >
                            Join Community
                        </button>
                    </div>
                </div>
            )}

            {/* Forward Share Popup */}
            <ForwardSharePopup
                isOpen={isSharePopupOpen}
                onClose={() => setIsSharePopupOpen(false)}
                onShare={handleForwardShare}
                groupId={communityId}
                currentGroupName={community?.name}
            />

            <SignInPopup
                open={showSignInPopup}
                onClose={() => setShowSignInPopup(false)}
            />
        </div>
    );
};

export default CommunityChat;